import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// Removed SizeIssueReport import - now using full page
import { 
  FaSearch, 
  FaBox, 
  FaTruck, 
  FaCheckCircle, 
  FaClock, 
  FaMapMarkerAlt, 
  FaEnvelope, 
  FaUser, 
  FaShieldAlt, 
  FaStar, 
  FaGift, 
  FaCrown, 
  FaMagic, 
  FaEye, 
  FaTimes, 
  FaCalendarAlt,
  FaEdit,
  FaRocket,
  FaAsterisk,
  FaGem,
  FaFire
} from 'react-icons/fa';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import SizeIssueStatus from '../components/SizeIssueStatus/SizeIssueStatus';
import './OrderTracking.css';

const OrderTracking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // Removed modal state - now using full page navigation

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!orderNumber.trim() || !email.trim()) {
      setError('Please enter both order number and email address');
      return;
    }

    setLoading(true);
    setError('');
    setSearched(true);

    try {
      // Query orders by order number and email
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('orderNumber', '==', orderNumber.trim()),
        where('userEmail', '==', email.trim())
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setError('No order found with this order number and email combination');
        setOrders([]);
        return;
      }

      // Get all matching orders
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt
      }));

      setOrders(ordersData);
    } catch (err) {
      console.error('Error searching for order:', err);
      setError('An error occurred while searching for your order. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Pre-fill form if coming from order history
  useEffect(() => {
    if (location.state?.orderNumber && location.state?.email) {
      setOrderNumber(location.state.orderNumber);
      setEmail(location.state.email);
      // Auto-search if both values are provided
      if (location.state.orderNumber && location.state.email) {
        handleSearch({ preventDefault: () => {} });
      }
    }
  }, [location.state]);


  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock className="vastre-status-icon vastre-pending" />;
      case 'confirmed':
        return <FaCheckCircle className="vastre-status-icon vastre-confirmed" />;
      case 'processing':
        return <FaBox className="vastre-status-icon vastre-processing" />;
      case 'shipped':
        return <FaTruck className="vastre-status-icon vastre-shipped" />;
      case 'delivered':
        return <FaCheckCircle className="vastre-status-icon vastre-delivered" />;
      case 'cancelled':
        return <FaTimes className="vastre-status-icon vastre-cancelled" />;
      default:
        return <FaClock className="vastre-status-icon vastre-pending" />;
    }
  };

  const handleSizeIssueReport = (order) => {
    navigate('/size-issue-report', { 
      state: { 
        orderData: {
          id: order.id,
          orderNumber: order.orderNumber,
          productName: order.items?.[0]?.name || 'Product',
          size: order.items?.[0]?.size || '',
          email: order.userEmail,
          phone: order.shippingAddress?.phone || '',
          userId: order.userId
        }
      } 
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { text: 'Pending', class: 'vastre-badge-pending' },
      confirmed: { text: 'Confirmed', class: 'vastre-badge-confirmed' },
      processing: { text: 'Processing', class: 'vastre-badge-processing' },
      shipped: { text: 'Shipped', class: 'vastre-badge-shipped' },
      delivered: { text: 'Delivered', class: 'vastre-badge-delivered' },
      cancelled: { text: 'Cancelled', class: 'vastre-badge-cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`vastre-status-badge ${config.class}`}>
        {config.text}
      </span>
    );
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'confirmed':
        return 'Confirmed';
      case 'processing':
        return 'Processing';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) {
      return 'N/A';
    }
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateTotal = (items) => {
    return items?.reduce((total, item) => total + (item.price * item.quantity), 0) || 0;
  };

  const getDeliveryDateInfo = (order) => {
    // Check if order has a specific delivery date set
    if (order.estimatedDeliveryDate) {
      const deliveryDate = order.estimatedDeliveryDate.toDate ? order.estimatedDeliveryDate.toDate() : new Date(order.estimatedDeliveryDate);
      return {
        label: 'Expected Delivery',
        date: deliveryDate,
        icon: 'delivery',
        isSpecific: true
      };
    }
    
    // If no specific delivery date, show generic 15-20 days
    if (!order.createdAt) return null;
    
    return {
      label: 'Expected Delivery',
      days: '15-20 days',
      icon: 'delivery',
      isSpecific: false
    };
  };

  const formatDeliveryDate = (date) => {
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  const handleReset = () => {
    setOrderNumber('');
    setEmail('');
    setOrders([]);
    setError('');
    setSearched(false);
  };

  return (
    <div className="vastre-tracking-page">
      <div className="vastre-tracking-container">
        {/* Search Section */}
        {!searched && (
          <div className="vastre-search-section">
            <div className="vastre-search-card">
              <div className="vastre-search-header">
                <h1>Track Your Order</h1>
                <p>Enter your order number and email to track your order status</p>
              </div>
              
              <form onSubmit={handleSearch} className="vastre-search-form">
                <div className="vastre-form-group">
                  <label htmlFor="orderNumber">Order Number</label>
                  <input
                    type="text"
                    id="orderNumber"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    placeholder="e.g., VV250109ABC"
                    required
                  />
                </div>
                
                <div className="vastre-form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                
                <button type="submit" className="vastre-search-btn" disabled={loading}>
                  {loading ? 'Searching...' : 'Track Order'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Results Section */}
        {searched && (
          <div className="vastre-results-section">
            <div className="vastre-results-header">
              <h2>Order Results</h2>
              <button onClick={handleReset} className="vastre-reset-btn">
                Search Another Order
              </button>
            </div>

            {loading && (
              <div className="vastre-loading">
                <div className="vastre-spinner"></div>
                <p>Searching for your order...</p>
              </div>
            )}

            {error && (
              <div className="vastre-error">
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && orders.length > 0 && (
              <div className="vastre-orders-grid">
                {orders.map((order) => (
                  <motion.div
                    key={order.id}
                    className="vastre-order-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => handleOrderClick(order)}
                  >
                    {/* Order Header */}
                    <div className="vastre-card-header">
                      <div className="vastre-order-number">
                        <FaStar className="vastre-order-icon" />
                        <h3>#{order.orderNumber}</h3>
                      </div>
                      <div className="vastre-order-status">
                        {getStatusBadge(order.status)}
                      </div>
                    </div>

                    {/* Product Display */}
                    <div className="vastre-product-display">
                      {order.items && order.items.length > 0 && (
                        <div className="vastre-product-image">
                          <img 
                            src={(() => {
                              const image = order.items[0].image;
                              if (image?.webp?.['400w']?.url) {
                                return image.webp['400w'].url;
                              } else if (image?.webp?.['800w']?.url) {
                                return image.webp['800w'].url;
                              } else if (image?.original?.url) {
                                return image.original.url;
                              } else {
                                 return image || '/images/fallbacks/placeholder-product.webp';
                              }
                            })()} 
                            alt={order.items[0].name || 'Product'}
                          />
                        </div>
                      )}
                      <div className="vastre-product-info">
                        <h4 className="vastre-product-name">
                          {order.items && order.items.length > 0 ? order.items[0].name : 'Product'}
                        </h4>
                        {order.items && order.items.length > 0 && order.items[0].variant && (
                          <p className="vastre-product-variant">
                            {order.items[0].variant.size && order.items[0].variant.color ? 
                              `${order.items[0].variant.size} • ${order.items[0].variant.color}` : 
                              order.items[0].variant.size || order.items[0].variant.color
                            }
                          </p>
                        )}
                        <div className="vastre-price-section">
                          <span className="vastre-price">₹{order.totalAmount?.toLocaleString() || calculateTotal(order.items || []).toLocaleString()}</span>
                          <span className="vastre-quantity">Qty: {order.items?.[0]?.quantity || 1}</span>
                        </div>
                      </div>
                    </div>

                    {/* Order Info */}
                    <div className="vastre-order-info">
                      <div className="vastre-info-item">
                        <FaCalendarAlt className="vastre-info-icon" />
                        <span>{formatDate(order.createdAt)}</span>
                      </div>
                      <div className="vastre-info-item">
                        <FaMapMarkerAlt className="vastre-info-icon" />
                        <span>{order.shippingAddress?.city}, {order.shippingAddress?.state}</span>
                      </div>
                      {getDeliveryDateInfo(order) && (
                        <div className="vastre-info-item vastre-delivery-info">
                          <FaTruck className="vastre-info-icon" />
                          <span>
                            <span className="vastre-delivery-label">Expected: </span>
                            {getDeliveryDateInfo(order).isSpecific 
                              ? formatDeliveryDate(getDeliveryDateInfo(order).date)
                              : getDeliveryDateInfo(order).days
                            }
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="vastre-card-action">
                      <motion.button 
                        className="vastre-view-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaEye />
                        View Details
                      </motion.button>
                      
                      {/* Size Issue Status - Shows report button or status */}
                      <SizeIssueStatus 
                        order={order} 
                        onReportIssue={handleSizeIssueReport}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <AnimatePresence>
        {showOrderModal && selectedOrder && (
          <motion.div
            className="vastre-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeOrderModal}
          >
            <motion.div
              className="vastre-order-modal"
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="vastre-modal-header">
                <div className="vastre-modal-title">
                  <FaCrown className="vastre-title-icon" />
                  <h2>Order #{selectedOrder.orderNumber}</h2>
                </div>
                <button className="vastre-modal-close" onClick={closeOrderModal}>
                  <FaTimes />
                </button>
              </div>

              {/* Modal Content */}
              <div className="vastre-modal-content">
                {/* Order Status */}
                <div className="vastre-status-section">
                  <div className="vastre-status-header">
                    <h3 className="vastre-status-title">Order Status</h3>
                    <div className="vastre-status-badge-modal">
                      {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                    </div>
                  </div>
                  <div className="vastre-status-description">
                    {selectedOrder.status === 'pending' && 'Your order is being reviewed by our team'}
                    {selectedOrder.status === 'confirmed' && 'Your order has been confirmed and is being prepared'}
                    {selectedOrder.status === 'processing' && 'Your order is being crafted with precision'}
                    {selectedOrder.status === 'shipped' && 'Your order is on its way to you'}
                    {selectedOrder.status === 'delivered' && 'Your order has been delivered successfully'}
                    {selectedOrder.status === 'cancelled' && 'Your order has been cancelled'}
                  </div>
                </div>

                {/* Order Details */}
                <div className="vastre-details-section">
                  <h3 className="vastre-details-title">Order Details</h3>
                  <div className="vastre-details-content">
                    <div className="vastre-detail-row">
                      <div className="vastre-detail-label">
                        <FaCalendarAlt className="vastre-detail-icon" />
                        <span>Order Date</span>
                      </div>
                      <div className="vastre-detail-value">{formatDate(selectedOrder.createdAt)}</div>
                    </div>
                    <div className="vastre-detail-row">
                      <div className="vastre-detail-label">
                        <FaMapMarkerAlt className="vastre-detail-icon" />
                        <span>Shipping Address</span>
                      </div>
                      <div className="vastre-detail-value">{selectedOrder.shippingAddress?.address}, {selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state} {selectedOrder.shippingAddress?.pinCode}</div>
                    </div>
                    {getDeliveryDateInfo(selectedOrder) && (
                      <div className="vastre-detail-row">
                        <div className="vastre-detail-label">
                          <FaCalendarAlt className={`vastre-detail-icon ${getDeliveryDateInfo(selectedOrder).icon}`} />
                          <span>{getDeliveryDateInfo(selectedOrder).label}</span>
                        </div>
                        <div className="vastre-detail-value">
                          {getDeliveryDateInfo(selectedOrder).isSpecific 
                            ? formatDeliveryDate(getDeliveryDateInfo(selectedOrder).date)
                            : getDeliveryDateInfo(selectedOrder).days
                          }
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Items */}
                <div className="vastre-items-section">
                  <h3 className="vastre-items-title">Order Items</h3>
                  <div className="vastre-items-content">
                    {selectedOrder.items?.map((item, index) => {
                      return (
                        <div key={index} className="vastre-item-card">
                          <div className="vastre-item-content">
                            <div className="vastre-item-image">
                              {item.image ? (
                                <img 
                                  src={(() => {
                                    if (item.image?.webp?.['400w']?.url) {
                                      return item.image.webp['400w'].url;
                                    } else if (item.image?.webp?.['800w']?.url) {
                                      return item.image.webp['800w'].url;
                                    } else if (item.image?.original?.url) {
                                      return item.image.original.url;
                                    } else {
                                      return item.image;
                                    }
                                  })()} 
                                  alt={item.name || 'Product'}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className="vastre-item-fallback" style={{ display: item.image ? 'none' : 'flex' }}>
                                <span className="vastre-fallback-icon">🛍️</span>
                              </div>
                            </div>
                            <div className="vastre-item-details">
                              <h4 className="vastre-item-name">{item.name}</h4>
                              {item.variant && (item.variant.size || item.variant.color) && (
                                <p className="vastre-item-variant">
                                  {item.variant.size && item.variant.color ? 
                                    `${item.variant.size} - ${item.variant.color}` : 
                                    item.variant.size || item.variant.color
                                  }
                                </p>
                              )}
                              <div className="vastre-item-price-quantity">
                                <span className="vastre-item-price">₹{item.price?.toLocaleString()}</span>
                                <span className="vastre-item-quantity">Qty: {item.quantity}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="vastre-summary-section">
                  <h3 className="vastre-summary-title">Order Summary</h3>
                  <div className="vastre-summary-content">
                    <div className="vastre-summary-row">
                      <span>Subtotal</span>
                      <span>₹{calculateTotal(selectedOrder.items || []).toLocaleString()}</span>
                    </div>
                    <div className="vastre-summary-row">
                      <span>Shipping</span>
                      <span>Free</span>
                    </div>
                    <div className="vastre-summary-row vastre-total">
                      <span>Total</span>
                      <span>₹{selectedOrder.totalAmount?.toLocaleString() || calculateTotal(selectedOrder.items || []).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

      {/* Size issue reporting now handled via full page navigation */}
      </AnimatePresence>
    </div>
  );
};

export default OrderTracking;